import {type TreeNode} from '@hm/fundamental'

/**
 * Represents a flattened tree node for list display when filtering.
 */
export interface FlatTreeNode {
  id: string
  label: string
  searchText: string
  /** Breadcrumb path showing the hierarchy (e.g., "Global > WSE > WE > Belgium") */
  path: string[]
  /** Original node data for rendering */
  originalNode: TreeNode
}

/**
 * Flattens a tree structure into a list of nodes with breadcrumb paths.
 * Useful for displaying filtered results as a simple list.
 */
export const flattenTreeNodes = (
  nodes: TreeNode[],
  path: string[] = [],
  searchTextMap?: Map<string, string>
): FlatTreeNode[] => {
  const result: FlatTreeNode[] = []

  for (const node of nodes) {
    const currentPath = [...path, node.label]
    const searchText = searchTextMap?.get(node.id) ?? node.label

    result.push({
      id: node.id,
      label: node.label,
      searchText,
      path: currentPath,
      originalNode: node
    })

    if (node.children && node.children.length > 0) {
      result.push(...flattenTreeNodes(node.children, currentPath, searchTextMap))
    }
  }

  return result
}

/**
 * Filters flat nodes by search term with fuzzy matching.
 * Returns nodes sorted by relevance (exact match > starts with > contains).
 */
export const filterFlatNodes = (nodes: FlatTreeNode[], searchTerm: string): FlatTreeNode[] => {
  const normalizedSearch = searchTerm.toLowerCase().trim()

  if (!normalizedSearch) {
    return nodes
  }

  return nodes
    .filter((node) => node.searchText.toLowerCase().includes(normalizedSearch))
    .sort((a, b) => {
      const aLower = a.searchText.toLowerCase()
      const bLower = b.searchText.toLowerCase()

      // Exact match first
      if (aLower === normalizedSearch && bLower !== normalizedSearch) return -1
      if (bLower === normalizedSearch && aLower !== normalizedSearch) return 1

      // Starts with second
      const aStartsWith = aLower.startsWith(normalizedSearch)
      const bStartsWith = bLower.startsWith(normalizedSearch)
      if (aStartsWith && !bStartsWith) return -1
      if (bStartsWith && !aStartsWith) return 1

      // Alphabetical as fallback
      return aLower.localeCompare(bLower)
    })
}

/**
 * Filters tree nodes recursively, keeping parent nodes if any child matches.
 * Used for tree view filtering.
 */
export const filterTreeNodes = (
  nodes: TreeNode[],
  searchTerm: string,
  searchTextMap?: Map<string, string>
): TreeNode[] => {
  const normalizedSearch = searchTerm.toLowerCase().trim()

  if (!normalizedSearch) {
    return nodes
  }

  return nodes.reduce<TreeNode[]>((acc, node) => {
    const searchText = searchTextMap?.get(node.id) ?? node.label
    const nodeMatches = searchText.toLowerCase().includes(normalizedSearch)

    const filteredChildren = node.children
      ? filterTreeNodes(node.children, searchTerm, searchTextMap)
      : []

    if (nodeMatches || filteredChildren.length > 0) {
      acc.push({
        ...node,
        children: filteredChildren.length > 0 ? filteredChildren : node.children
      })
    }

    return acc
  }, [])
}

/**
 * Updates the expanded state to expand all nodes that contain matches.
 */
export const expandMatchingNodes = (
  nodes: TreeNode[],
  searchTerm: string,
  searchTextMap?: Map<string, string>
): Record<string, boolean> => {
  const expanded: Record<string, boolean> = {}
  const normalizedSearch = searchTerm.toLowerCase().trim()

  if (!normalizedSearch) {
    return expanded
  }

  const hasMatchingDescendant = (node: TreeNode): boolean => {
    const searchText = searchTextMap?.get(node.id) ?? node.label
    if (searchText.toLowerCase().includes(normalizedSearch)) {
      return true
    }
    return node.children?.some((child) => hasMatchingDescendant(child)) ?? false
  }

  const processNode = (node: TreeNode) => {
    if (node.children && node.children.length > 0) {
      const hasMatch = node.children.some((child) => hasMatchingDescendant(child))
      if (hasMatch) {
        expanded[node.id] = true
      }
      node.children.forEach(processNode)
    }
  }

  nodes.forEach(processNode)
  return expanded
}
